import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

// DESIGN.md, Buttons: one dominant action per working region. `default` is
// the citron primary with the cut corner (the `halo-primary` utility in
// app/globals.css draws it on pseudo-elements, so the focus outline and hit
// area stay rectangular). `outline` is the secondary; `ghost` and `link` are
// tertiary. Pressing moves the face down 1px.
const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-[background-color,box-shadow,color,transform] duration-120 ease-halo outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-halo-accent-ink disabled:cursor-not-allowed aria-invalid:outline-halo-danger [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "halo-primary",
        destructive:
          "bg-halo-danger text-halo-on-danger shadow-xs hover:bg-[color-mix(in_srgb,var(--halo-danger)_90%,var(--halo-ink))] active:translate-y-px disabled:bg-halo-inset disabled:text-halo-secondary disabled:shadow-none",
        outline:
          "bg-halo-raised text-halo-ink shadow-[inset_0_0_0_1px_var(--halo-control-edge),var(--halo-shadow-control)] hover:bg-halo-hover active:translate-y-px active:shadow-[inset_0_0_0_1px_var(--halo-control-edge)] disabled:bg-halo-inset disabled:text-halo-secondary disabled:shadow-[inset_0_0_0_1px_var(--halo-line)] disabled:translate-y-0",
        secondary:
          "bg-halo-inset text-halo-ink shadow-[inset_0_0_0_1px_var(--halo-line)] hover:bg-halo-hover active:translate-y-px disabled:text-halo-secondary",
        ghost:
          "text-halo-secondary hover:bg-halo-hover hover:text-halo-ink disabled:text-halo-muted",
        link: "text-halo-accent-ink underline-offset-[3px] hover:underline disabled:text-halo-muted",
      },
      size: {
        default: "h-9 px-3.5 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-xs px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-11 px-5 has-[>svg]:px-4",
        icon: "size-9 [&_svg:not([class*='size-'])]:size-5",
        "icon-xs": "size-6 rounded-xs [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-11 [&_svg:not([class*='size-'])]:size-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
